import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  ListItemButton,
  Chip,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import logoImage from "../assets/mainlogo1.png";
import MenuIcon from "@mui/icons-material/Menu";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PublicIcon from "@mui/icons-material/Public";

import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// ─── Theme ────────────────────────────────────────────────────────────────────
const navTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    background: { paper: "#ffffff" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: { fontFamily: "'Sora', 'Segoe UI', sans-serif" },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const slideIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.92)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  borderBottom: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)",
  color: theme.palette.text.primary,
  animation: `${fadeDown} 0.4s ease both`,
}));

const NavLink = styled(Box, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  gap: 7,
  padding: "7px 14px",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "'Sora', sans-serif",
  cursor: "pointer",
  textDecoration: "none",
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  background: active ? alpha(theme.palette.primary.main, 0.08) : "transparent",
  border: `1px solid ${
    active ? alpha(theme.palette.primary.main, 0.18) : "transparent"
  }`,
  transition: "all 0.18s ease",
  "&:hover": {
    color: theme.palette.primary.main,
    background: alpha(theme.palette.primary.main, 0.06),
    borderColor: alpha(theme.palette.primary.main, 0.15),
    transform: "translateY(-1px)",
  },
}));

const AdminAvatarBtn = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "6px 12px 6px 6px",
  borderRadius: 40,
  border: `1px solid ${alpha("#000", 0.1)}`,
  background: "#fff",
  cursor: "pointer",
  transition: "all 0.18s ease",
  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.3),
    background: alpha(theme.palette.primary.main, 0.03),
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 14,
    border: `1px solid ${alpha("#000", 0.08)}`,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1), 0 1px 4px rgba(0,0,0,0.06)",
    minWidth: 200,
    padding: "6px",
    fontFamily: "'Sora', sans-serif",
    marginTop: 8,
    backdropFilter: "blur(12px)",
    background: "rgba(255,255,255,0.97)",
  },
}));

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (p) => p !== "danger",
})(({ theme, danger }) => ({
  borderRadius: 9,
  padding: "9px 12px",
  fontFamily: "'Sora', sans-serif",
  fontSize: 13,
  fontWeight: 600,
  color: danger ? theme.palette.error.main : theme.palette.text.primary,
  gap: 2,
  transition: "all 0.15s ease",
  "&:hover": {
    background: danger
      ? alpha(theme.palette.error.main, 0.07)
      : alpha(theme.palette.primary.main, 0.06),
    color: danger ? theme.palette.error.main : theme.palette.primary.main,
  },
}));

const DrawerWrapper = styled(Box)(() => ({
  width: 300,
  height: "100%",
  background: "linear-gradient(160deg, #EEF2FF 0%, #F5F7FF 100%)",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'Sora', sans-serif",
}));

const DrawerNavItem = styled(ListItemButton, {
  shouldForwardProp: (p) => p !== "active",
})(({ theme, active }) => ({
  borderRadius: 12,
  marginBottom: 4,
  padding: "11px 16px",
  animation: `${slideIn} 0.35s ease both`,
  background: active ? alpha(theme.palette.primary.main, 0.1) : "transparent",
  border: `1px solid ${
    active ? alpha(theme.palette.primary.main, 0.2) : "transparent"
  }`,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: "all 0.18s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.07),
    color: theme.palette.primary.main,
    borderColor: alpha(theme.palette.primary.main, 0.15),
  },
}));

// ─── Nav items ────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { label: "Dashboard", icon: DashboardIcon, to: "/dashboard" },
  { label: "News", icon: NewspaperIcon, to: "/admin-news-country" },
  { label: "Exams", icon: MenuBookIcon, to: "/admin-exam-country" },
  { label: "Countries", icon: PublicIcon, to: "/admin-country" },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.removeItem("authToken");
      navigate("/login");
    }
    setAnchorEl(null);
  };

  // ── Drawer ──
  const drawer = (
    <DrawerWrapper>
      {/* Drawer header */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              overflow: "hidden",
              border: `1px solid ${alpha("#000", 0.08)}`,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="Logo"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 800,
                color: "text.primary",
                fontFamily: "'Sora', sans-serif",
                lineHeight: 1.1,
              }}
            >
              LiteUpper
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: "primary.main",
                fontFamily: "'Sora', sans-serif",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Admin Panel
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => setMobileOpen(false)}
          sx={{
            background: alpha("#000", 0.05),
            borderRadius: "8px",
            "&:hover": { background: alpha("#000", 0.08) },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Divider sx={{ mx: 2, borderColor: alpha("#000", 0.07) }} />

      {/* Nav links */}
      <Box sx={{ px: 2, pt: 2, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            color: "text.disabled",
            mb: 1.5,
            px: 1,
            fontFamily: "'Sora', sans-serif",
          }}
        >
          Navigation
        </Typography>
        {NAV_ITEMS.map((item, i) => {
          const IconComp = item.icon;
          const active = location.pathname === item.to;
          return (
            <DrawerNavItem
              key={item.label}
              component={Link}
              to={item.to}
              active={active ? 1 : 0}
              onClick={() => setMobileOpen(false)}
              sx={{ animationDelay: `${i * 55}ms` }}
            >
              <IconComp
                sx={{
                  fontSize: 16,
                  mr: 1.5,
                  color: active ? "primary.main" : "text.disabled",
                }}
              />
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                {item.label}
              </Typography>
              {active && (
                <Box
                  sx={{
                    ml: "auto",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "primary.main",
                    bgcolor: "primary.main",
                  }}
                />
              )}
            </DrawerNavItem>
          );
        })}
      </Box>

      {/* Drawer footer */}
      <Box sx={{ px: 2, pb: 3 }}>
        <Divider sx={{ mb: 2, borderColor: alpha("#000", 0.07) }} />

        {/* Admin info row */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1, mb: 2 }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: "primary.main",
              fontSize: 14,
              fontFamily: "'Sora', sans-serif",
              fontWeight: 800,
            }}
          >
            A
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "text.primary",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Administrator
            </Typography>
            <Chip
              label="Super Admin"
              size="small"
              sx={{
                fontSize: 9,
                fontWeight: 700,
                height: 18,
                fontFamily: "'Sora', sans-serif",
                background: alpha("#2563EB", 0.1),
                color: "#2563EB",
                border: `1px solid ${alpha("#2563EB", 0.2)}`,
              }}
            />
          </Box>
        </Box>

        <DrawerNavItem
          onClick={handleLogout}
          sx={{
            color: "error.main",
            borderColor: "transparent",
            background: alpha("#DC2626", 0.04),
            "&:hover": {
              background: alpha("#DC2626", 0.08),
              borderColor: alpha("#DC2626", 0.15),
              color: "error.main",
            },
            animationDelay: `${NAV_ITEMS.length * 55}ms`,
          }}
        >
          <LogoutIcon sx={{ fontSize: 16, mr: 1.5, color: "error.main" }} />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "'Sora', sans-serif",
              color: "error.main",
            }}
          >
            Logout
          </Typography>
        </DrawerNavItem>
      </Box>
    </DrawerWrapper>
  );

  return (
    <ThemeProvider theme={navTheme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <StyledAppBar position="sticky" elevation={0}>
        <Toolbar
          sx={{
            minHeight: { xs: 64, md: 68 },
            px: { xs: 2, md: 4 },
            justifyContent: "space-between",
          }}
        >
          {/* ── Logo ── */}
          <Box
            component={Link}
            to="/admin-dashboard"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              textDecoration: "none",
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "11px",
                overflow: "hidden",
                border: `1px solid ${alpha("#000", 0.08)}`,
                flexShrink: 0,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              }}
            >
              <Box
                component="img"
                src={logoImage}
                alt="LiteUpper"
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: "text.primary",
                  fontFamily: "'Sora', sans-serif",
                  lineHeight: 1.1,
                }}
              >
                LiteUpper
              </Typography>
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: "primary.main",
                  fontFamily: "'Sora', sans-serif",
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                }}
              >
                Admin Panel
              </Typography>
            </Box>
          </Box>

          {/* ── Desktop nav ── */}
          {!isMobile ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {NAV_ITEMS.map((item) => {
                const IconComp = item.icon;
                const active = location.pathname === item.to;
                return (
                  <NavLink
                    key={item.label}
                    component={Link}
                    to={item.to}
                    active={active ? 1 : 0}
                  >
                    <IconComp sx={{ fontSize: 14 }} />
                    {item.label}
                  </NavLink>
                );
              })}

              <Box
                sx={{
                  width: 1,
                  height: 28,
                  background: alpha("#000", 0.1),
                  mx: 1.5,
                }}
              />

              {/* Admin profile pill */}
              <AdminAvatarBtn onClick={(e) => setAnchorEl(e.currentTarget)}>
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: "primary.main",
                    fontSize: 12,
                    fontWeight: 800,
                    fontFamily: "'Sora', sans-serif",
                  }}
                >
                  A
                </Avatar>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "text.primary",
                      fontFamily: "'Sora', sans-serif",
                      lineHeight: 1.1,
                    }}
                  >
                    Admin
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: "text.disabled",
                      fontFamily: "'Sora', sans-serif",
                    }}
                  >
                    Super Admin
                  </Typography>
                </Box>
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 15,
                    color: "text.disabled",
                    ml: 0.5,
                    transition: "transform 0.2s ease",
                    transform: Boolean(anchorEl)
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  }}
                />
              </AdminAvatarBtn>
            </Box>
          ) : (
            /* ── Mobile hamburger ── */
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{
                background: alpha("#2563EB", 0.07),
                borderRadius: "10px",
                border: `1px solid ${alpha("#2563EB", 0.15)}`,
                "&:hover": { background: alpha("#2563EB", 0.12) },
              }}
            >
              <MenuIcon sx={{ fontSize: 20, color: "primary.main" }} />
            </IconButton>
          )}
        </Toolbar>
      </StyledAppBar>

      {/* ── Desktop profile dropdown ── */}
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5, mb: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                fontSize: 13,
                fontWeight: 800,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              A
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "text.primary",
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Administrator
              </Typography>
              <Chip
                label="Super Admin"
                size="small"
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  height: 18,
                  fontFamily: "'Sora', sans-serif",
                  background: alpha("#2563EB", 0.1),
                  color: "#2563EB",
                  border: `1px solid ${alpha("#2563EB", 0.2)}`,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 0.5, borderColor: alpha("#000", 0.07) }} />

        <StyledMenuItem onClick={handleLogout} danger={1}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <LogoutIcon sx={{ fontSize: 15, color: "error.main" }} />
          </ListItemIcon>
          <ListItemText>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'Sora', sans-serif",
                color: "error.main",
              }}
            >
              Logout
            </Typography>
          </ListItemText>
        </StyledMenuItem>
      </StyledMenu>

      {/* ── Mobile drawer ── */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            borderRadius: "20px 0 0 20px",
            overflow: "hidden",
            boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* ── Backdrop ── */}
      {mobileOpen && (
        <Box
          onClick={() => setMobileOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.35)",
            zIndex: 1199,
            backdropFilter: "blur(2px)",
          }}
        />
      )}
    </ThemeProvider>
  );
};

export default AdminNavbar;
